/**
 * SI-2-01 — pure session client core (PRD §5 client SDK, §6.3 attach/reconnect).
 *
 * No React, no IO, no clock. A transport feeds parsed host messages in; the
 * core returns the next public snapshot and any side-effect intents (resync,
 * disconnect). Unit tests drive it with synthetic notifications; the React
 * hook (`use-session.ts`) wires WebSocket + gapless + this reducer pipeline.
 *
 * Invariants enforced here:
 *   · Server-wins (S-D4/P7): `act` is intent-only; state advances only on
 *     authoritative `session.action` notifications. No optimistic merge.
 *   · Gapless seq (SI-1-02): a hole forces `resync` rather than reducing.
 *   · Compaction (R-S4): `session.compacted` forces re-attach from floor.
 */

import {
  observeGaplessSeq,
  type GaplessObserveResult,
} from "./gapless";
import type {
  SessionAttachResponse,
  SessionMode,
  SessionParticipant,
} from "../api/contracts/sessions";

export type ConnectionState =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error"
  | "closed";

/** Authoritative action as the host fans it out (session.action params). */
export type SessionClientAction = {
  seq: number;
  kind: string;
  payload: unknown;
  actorId: string;
  ts: string;
};

export type SessionClientReduceFn<S> = (
  state: S,
  action: SessionClientAction,
) => S;

export type SessionClientSnapshot<S = unknown> = {
  urn: string;
  state: S | null;
  headSeq: number;
  /** Last contiguous seq reduced (null before any action observed). */
  lastSeq: number | null;
  participants: SessionParticipant[];
  capabilities: string[];
  connectionState: ConnectionState;
  error: string | null;
  /** When set, the hook should re-attach with this afterSeq (or null for full). */
  pendingResync: { afterSeq: number | null; reason: string } | null;
};

export type SessionClientConfig<S = unknown> = {
  urn: string;
  mode?: SessionMode;
  reduce?: SessionClientReduceFn<S>;
  initialState?: () => S;
};

/**
 * Apply attach payload: seed state from snapshot (or initialState), then
 * reduce the ordered replay window. Gapless starts from snapshotSeq so the
 * first action after the floor is accepted as first observation.
 */
export function applyAttachPayload<S = unknown>(
  prev: SessionClientSnapshot<S>,
  attach: SessionAttachResponse,
  config: Pick<SessionClientConfig<S>, "reduce" | "initialState">,
): SessionClientSnapshot<S> {
  const reduce = config.reduce;
  const initial =
    attach.snapshot != null
      ? (attach.snapshot as S)
      : config.initialState
        ? config.initialState()
        : null;

  let state: S | null = initial;
  let lastSeq: number | null =
    attach.snapshotSeq > 0 ? attach.snapshotSeq : null;

  for (const row of attach.actions) {
    const action: SessionClientAction = {
      seq: row.seq,
      kind: row.kind,
      payload: row.payload,
      actorId: row.actorId,
      ts: row.ts,
    };
    const gap = observeGaplessSeq(lastSeq, action.seq);
    if (gap.status === "resync") {
      // Replay window from the host must itself be gapless; if not, demand
      // a full re-attach rather than silently skipping.
      return {
        ...prev,
        urn: attach.urn,
        participants: attach.participants,
        capabilities: attach.capabilities,
        connectionState: "connected",
        error: null,
        headSeq: attach.headSeq,
        lastSeq,
        state,
        pendingResync: {
          afterSeq: lastSeq,
          reason: `attach-replay-${gap.reason}`,
        },
      };
    }
    lastSeq = gap.lastSeq;
    if (reduce && state !== null) {
      state = reduce(state, action);
    } else if (reduce && state === null && config.initialState) {
      state = reduce(config.initialState(), action);
    }
  }

  return {
    urn: attach.urn,
    state,
    headSeq: attach.headSeq,
    lastSeq,
    participants: attach.participants,
    capabilities: attach.capabilities,
    connectionState: "connected",
    error: null,
    pendingResync: null,
  };
}

/**
 * Observe one `session.action` notification. Server-wins: only this path
 * advances reduced state (never the act RPC result).
 */
export function applyActionNotification<S = unknown>(
  prev: SessionClientSnapshot<S>,
  params: {
    seq: number;
    action: { kind: string; payload: unknown };
    actorId: string;
    ts: string;
  },
  config: Pick<SessionClientConfig<S>, "reduce" | "initialState">,
): SessionClientSnapshot<S> {
  const gap: GaplessObserveResult = observeGaplessSeq(prev.lastSeq, params.seq);
  if (gap.status === "resync") {
    return {
      ...prev,
      pendingResync: {
        afterSeq: prev.lastSeq,
        reason: gap.reason,
      },
    };
  }

  // Duplicate notification (same seq as lastSeq): at-least-once fan-out.
  // Do not re-reduce — server-wins already applied this action once.
  if (prev.lastSeq != null && params.seq === prev.lastSeq) {
    return {
      ...prev,
      pendingResync: null,
    };
  }

  const action: SessionClientAction = {
    seq: params.seq,
    kind: params.action.kind,
    payload: params.action.payload,
    actorId: params.actorId,
    ts: params.ts,
  };

  let state = prev.state;
  if (config.reduce) {
    if (state === null && config.initialState) {
      state = config.reduce(config.initialState(), action);
    } else if (state !== null) {
      state = config.reduce(state, action);
    }
  }

  return {
    ...prev,
    state,
    lastSeq: gap.lastSeq,
    headSeq: Math.max(prev.headSeq, params.seq),
    pendingResync: null,
  };
}

/** Host→client presence: join / leave / idle. */
export function applyPresenceNotification<S = unknown>(
  prev: SessionClientSnapshot<S>,
  params: {
    event: "joined" | "left" | "idle";
    participant: SessionParticipant;
  },
): SessionClientSnapshot<S> {
  const { event, participant } = params;
  let participants = prev.participants;
  if (event === "joined") {
    const without = participants.filter((p) => p.actorId !== participant.actorId);
    participants = [...without, participant];
  } else if (event === "left") {
    participants = participants.filter((p) => p.actorId !== participant.actorId);
  } else {
    // idle: keep the row (mode unchanged); presence is informational.
    const i = participants.findIndex((p) => p.actorId === participant.actorId);
    if (i >= 0) {
      participants = participants.slice();
      participants[i] = participant;
    } else {
      participants = [...participants, participant];
    }
  }
  return { ...prev, participants };
}

/**
 * Compaction floor advanced — client holding afterSeq < floor must re-attach
 * from snapshot (never silently gapped; R-S4).
 */
export function applyCompactedNotification<S = unknown>(
  prev: SessionClientSnapshot<S>,
  params: { snapshotFloor: number },
): SessionClientSnapshot<S> {
  const last = prev.lastSeq ?? 0;
  if (last < params.snapshotFloor) {
    return {
      ...prev,
      pendingResync: {
        afterSeq: null,
        reason: "compacted",
      },
    };
  }
  return prev;
}

export function initialSessionSnapshot<S = unknown>(
  urn: string,
  connectionState: ConnectionState = "idle",
): SessionClientSnapshot<S> {
  return {
    urn,
    state: null,
    headSeq: 0,
    lastSeq: null,
    participants: [],
    capabilities: [],
    connectionState,
    error: null,
    pendingResync: null,
  };
}

/**
 * Mint an idempotency key for session.act when the caller did not supply one.
 * Pure given a seed string (caller supplies crypto/random or a counter).
 */
export function makeActIdempotencyKey(seed: string): string {
  return `act:${seed}`;
}
